import axios from "axios";
import config from "./config";

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

export const userRequest = axios.create({
  baseURL: DOMAIN,
  withCredentials: true,
});

// Instance spéciale pour refresh token
const refreshClient = axios.create({
  baseURL: DOMAIN,
  withCredentials: true,
});

// ⚡ Cache mémoire du token : accessible SYNCHRONOUSMENT dès que loginSuccess
// est dispatché. Résout la race condition redux-persist (localStorage mis à
// jour asynchronement).
//
// Flux avant (cassé) :
//   1. dispatch(loginSuccess(res.data)) → Redux state mis à jour
//   2. navigate('/') déclenche le rendu de App.js
//   3. App.js useEffect → fetchMenu() → getMenusAllOne(userId) via userRequest
//   4. userRequest.interceptors.request lit localStorage → PAS ENCORE de token
//      (redux-persist n'a pas fini d'écrire) → 401 → refresh → 401 → logout
//
// Flux après (corrigé) :
//   1. setAccessToken(res.data.accessToken)appelé SYNCHRONOUSMENT dans apiCalls
//   2. dispatch(loginSuccess(res.data)) → Redux state mis à jour
//   3. navigate('/') → App.js useEffect → fetchMenu() → userRequest
//   4. userRequest.interceptors.request lit d'abord la variable mémoire →
//      token présent → Authorization: Bearer envoyé → 200 OK
let cachedToken = null;

export const setAccessToken = (token) => {
  cachedToken = token || null;
};

export const getAccessToken = () => {
  // 1. D'abord la variable mémoire (synchrone)
  if (cachedToken) return cachedToken;
  // 2. Fallback localStorage (pour rechargement de page)
  try {
    const persisted = JSON.parse(localStorage.getItem("persist:root"));
    const user = persisted?.user ? JSON.parse(persisted.user) : null;
    return user?.currentUser?.accessToken || null;
  } catch {
    return null;
  }
};

// Flag pour éviter les refresh simultanés
let isRefreshing = false;
let refreshSubscribers = [];

// Ajouter des callbacks à exécuter après refresh
function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

// ===================== REQUEST INTERCEPTOR =====================
userRequest.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ===================== RESPONSE INTERCEPTOR =====================
userRequest.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) return Promise.reject(error);

    // 401 → access token expiré
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/refresh-token")
    ) {
      const currentToken = getAccessToken();
      if (!currentToken) {
        // Pas de token du tout → pas la peine de tenter refresh
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        // Si un refresh est déjà en cours, on attend qu'il se termine
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(userRequest(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const res = await refreshClient.get("/api/auth/refresh-token");
        const newAccessToken = res.data.accessToken;

        if (!newAccessToken) throw new Error("No token");

        // Mettre à jour le cache mémoire
        setAccessToken(newAccessToken);

        // Mettre à jour le localStorage
        const persisted = JSON.parse(localStorage.getItem("persist:root"));
        const user = JSON.parse(persisted.user);
        user.currentUser.accessToken = newAccessToken;
        persisted.user = JSON.stringify(user);
        localStorage.setItem("persist:root", JSON.stringify(persisted));

        // Notifier toutes les requêtes en attente
        onRefreshed(newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return userRequest(originalRequest);
      } catch {
        // refresh échoué → logout
        cachedToken = null;
        localStorage.removeItem("persist:root");
        window.location.href = "/login";
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);